<script setup lang="ts">
import { useForm } from "@inertiajs/vue3";
import { Head } from "@unhead/vue/components";
import { Square, Bath, Layers, MapPin } from "lucide-vue-next";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import RealityMarketingLayout from "@/Layouts/RealityMarketingLayout.vue";
import route from "ziggy-js";

const props = defineProps<{
    accessGranted: boolean;
}>();

const form = useForm({
    access_code: "",
});

const submitAccessCode = () => {
    form.post(route("reality.access"), {
        preserveScroll: true,
        onError: () => {
            form.reset("access_code");
        },
    });
};

const ctaHref = props.accessGranted ? route("register") : "#pristup";

const features = [
    {
        title: "Vlastní URL pro každou nemovitost",
        description:
            "Sdílejte jednoduchý rezervační odkaz v inzerátu na Sreality, v e-mailové signatuře nebo přímo v SMS klientovi.",
    },
    {
        title: "Kalendář bez kolizí",
        description:
            "Synchronizace s Google, Apple a Outlook kalendářem v reálném čase. Žádné dvojrezervace, žádné překryvy s vašimi schůzkami.",
    },
    {
        title: "Automatická potvrzení a připomínky",
        description:
            "Klient dostane potvrzení do 30 sekund a připomínku den před prohlídkou. Méně no-show, méně dotazů, méně práce pro vás.",
    },
    {
        title: "Předpřipraveno pro makléře",
        description:
            "E-maily, šablony i pole pro adresu prohlídky — vše už nastavené tak, jak realitní makléři pracují. Spustíte za pět minut.",
    },
    {
        title: "Statistiky prohlídek",
        description:
            "Vidíte, kolik lidí kliklo na odkaz, kolik si rezervovalo termín a kolik z nich opravdu dorazilo. Data pro každý inzerát zvlášť.",
    },
    {
        title: "Vlastní portfolio na jedné stránce",
        description:
            "Společná veřejná stránka se všemi vašimi aktivními inzeráty. Klienti listují a rezervují prohlídky přímo odsud.",
    },
];

const steps = [
    {
        title: "Přidejte nemovitost",
        body: "Foto, adresa, cena. Rezervační stránka je hotová za 30 sekund.",
    },
    {
        title: "Připojte svůj kalendář",
        body: "Google, Apple nebo Outlook — propojení jedním kliknutím. Žádné kolize, žádné dvojrezervace.",
    },
    {
        title: "Sdílejte rezervační odkaz",
        body: "V inzerátu na Sreality, v e-mailové signatuře, v SMS. Klient si sám vybere termín, vy dostanete potvrzení.",
    },
];

const portfolioCards = [
    {
        price: "6 500 000 Kč",
        addr: "Vinohradská 42, Praha 2",
        m2: "68 m²",
        rooms: "3 pokoje",
        img: "https://images.unsplash.com/photo-1493809842364-78817add7ffb?auto=format&fit=crop&w=600&q=80",
    },
    {
        price: "4 200 000 Kč",
        addr: "Korunní 15, Praha 3",
        m2: "45 m²",
        rooms: "2 pokoje",
        img: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=600&q=80",
    },
    {
        price: "8 900 000 Kč",
        addr: "Na Poříčí 30, Praha 1",
        m2: "92 m²",
        rooms: "4 pokoje",
        img: "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&w=600&q=80",
    },
];

const faqItems = [
    {
        q: "Vidí klient můj soukromý kalendář?",
        a: "Ne. Klient vidí pouze volné časy, které sami povolíte pro daný typ prohlídky. Vaše soukromé události zůstávají skryté — Reality si z kalendáře jen vybere, kdy nejste k dispozici.",
    },
    {
        q: "Co když si dva klienti zarezervují stejný čas?",
        a: "Není to možné. Rezervační slot se uzamkne v momentě, kdy ho první klient potvrdí. Druhý klient vidí daný čas jako obsazený a vybere jiný termín.",
    },
    {
        q: "Můžu zrušit kdykoliv?",
        a: "Ano. Předplatné je měsíční a můžete ho kdykoliv ukončit přímo v aplikaci. Žádná výpovědní lhůta, žádné storno poplatky.",
    },
    {
        q: "Co se stane s aktivními rezervacemi, když přestanu platit?",
        a: "Klienti, kteří už mají rezervaci potvrzenou, dostanou prohlídku normálně — termíny zůstávají platné. Vy ale ztratíte možnost přidávat nové nemovitosti a klienti nebudou moci nově rezervovat, dokud předplatné neobnovíte.",
    },
    {
        q: "Funguje to s mým inzerátem na Sreality?",
        a: "Ano. Do popisku inzerátu zkopírujete rezervační odkaz na danou nemovitost. Když ho klient na Sreality vidí, klikne a rezervuje si prohlídku přímo u vás — bez čekání na váš telefon. Hlubší integraci se Sreality plánujeme do konce roku.",
    },
    {
        q: "Vytvoří se prohlídka i v mém kalendáři?",
        a: "Ano. Potvrzená prohlídka se automaticky vytvoří jako událost ve vašem připojeném kalendáři (Google, Apple nebo Outlook) — i s adresou, kontaktem na klienta a poznámkou. Nemusíte nic přepisovat.",
    },
    {
        q: "Mohu si přizpůsobit potvrzovací e-mail?",
        a: "Ano. Šablony jsou připravené, ale text, podpis i logo si můžete kdykoliv upravit. V plánu Business máte k dispozici i vlastní HTML šablony.",
    },
    {
        q: "Jaké platební metody přijímáte?",
        a: "Platební karty (Visa, Mastercard) přes bezpečnou bránu Stripe. Pro plán Business nabízíme i platbu fakturou na bankovní převod — stačí napsat.",
    },
];

const timeSlots = ["9:00", "10:00", "11:00", "14:00", "15:00", "16:00"];

const proFeatures = [
    "Rezervační stránka pro každou nemovitost",
    "Synchronizace Google, Apple, Outlook",
    "Automatické potvrzení a připomínky",
    "Předpřipravené e-mailové šablony",
    "Statistiky prohlídek",
    "Podpora e-mailem",
];

const businessFeatures = [
    "Vše z plánu Pro",
    "Neomezený počet aktivních nemovitostí",
    "Vlastní HTML e-mailové šablony",
    "Prioritní podpora",
    "Platba fakturou na bankovní převod",
];
</script>

<template>
    <Head title="Reality Zaptime — rezervační systém pro realitní makléře" />

    <RealityMarketingLayout>
        <!-- HERO -->
        <section class="px-6 pt-20 pb-24 max-w-6xl lg:pt-32 lg:pb-32 mx-auto">
            <div class="gap-16 lg:grid-cols-12 lg:items-center grid">
                <div class="lg:col-span-7">
                    <p
                        class="text-xs font-medium tracking-widest text-iron-400 uppercase"
                    >
                        Reality · od Zaptime
                    </p>

                    <h1
                        class="mt-6 font-serif font-normal tracking-tight text-iron-900 text-5xl sm:text-6xl lg:text-7xl leading-[1.05]"
                    >
                        Prohlídky bez<br />
                        <span class="text-iron-500 italic">jediné</span>
                        SMSky.
                    </h1>

                    <p
                        class="mt-8 text-lg text-iron-600 leading-relaxed max-w-xl"
                    >
                        Sdílejte rezervační odkaz v inzerátu, v e-mailu nebo v
                        signatuře. Klient si sám vybere termín — synchronizovaně
                        s vaším kalendářem (Google, Apple, Outlook).
                    </p>

                    <div
                        class="gap-5 mt-10 sm:flex-row sm:items-center flex flex-col"
                    >
                        <a
                            :href="ctaHref"
                            class="bg-iron-900 px-6 py-3 text-sm font-medium text-white hover:bg-iron-800 inline-flex items-center justify-center rounded-full transition-colors"
                        >
                            Vyzkoušet zdarma
                        </a>
                        <span class="text-sm text-iron-500">
                            Plná funkčnost. Bez karty, bez časového limitu.
                        </span>
                    </div>
                </div>

                <div class="lg:col-span-5">
                    <div
                        class="rounded-sm bg-white border-iron-200 overflow-hidden border"
                    >
                        <img
                            src="https://images.unsplash.com/photo-1493809842364-78817add7ffb?auto=format&fit=crop&w=1200&q=80"
                            alt="Slunný byt 3+kk s lodžií"
                            class="h-64 w-full object-cover"
                        />

                        <div class="p-6">
                            <div
                                class="gap-3 flex items-baseline justify-between"
                            >
                                <h3 class="text-sm font-medium text-iron-900">
                                    Vinohradská 42, Praha 2
                                </h3>
                                <span
                                    class="text-xs tracking-wider text-iron-400 uppercase"
                                >
                                    Prodej
                                </span>
                            </div>
                            <p class="mt-2 font-serif text-2xl text-iron-900">
                                6 500 000 Kč
                            </p>
                            <div
                                class="gap-4 mt-3 text-xs text-iron-500 flex flex-wrap items-center"
                            >
                                <span class="gap-1.5 inline-flex items-center">
                                    <Square class="h-3 w-3" />
                                    68 m²
                                </span>
                                <span class="gap-1.5 inline-flex items-center">
                                    <Bath class="h-3 w-3" />
                                    3 pokoje
                                </span>
                                <span class="gap-1.5 inline-flex items-center">
                                    <Layers class="h-3 w-3" />
                                    4. patro
                                </span>
                            </div>

                            <div class="border-iron-100 pt-5 mt-6 border-t">
                                <p
                                    class="text-xs font-medium tracking-wider text-iron-400 uppercase"
                                >
                                    Rezervovat prohlídku
                                </p>
                                <p class="mt-2 text-sm text-iron-700">
                                    Středa 21. května
                                </p>

                                <div class="gap-2 mt-3 grid grid-cols-3">
                                    <button
                                        v-for="(slot, i) in timeSlots"
                                        :key="slot"
                                        type="button"
                                        :class="[
                                            'rounded-sm py-2 text-xs font-medium border transition-colors',
                                            i === 2
                                                ? 'bg-iron-900 text-white border-iron-900'
                                                : 'bg-white text-iron-700 border-iron-200 hover:border-iron-400',
                                        ]"
                                    >
                                        {{ slot }}
                                    </button>
                                </div>

                                <button
                                    type="button"
                                    class="bg-iron-900 px-4 py-3 text-sm font-medium text-white hover:bg-iron-800 mt-5 w-full rounded-full transition-colors"
                                >
                                    Rezervovat
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- HOW IT WORKS -->
        <section class="px-6 py-24 border-iron-100 border-t">
            <div class="max-w-6xl mx-auto">
                <div class="max-w-2xl">
                    <p
                        class="text-xs font-medium tracking-widest text-iron-400 uppercase"
                    >
                        Jak to funguje
                    </p>
                    <h2
                        class="mt-4 font-serif font-normal tracking-tight text-iron-900 leading-tight text-4xl sm:text-5xl"
                    >
                        Hotovo za pět minut.
                    </h2>
                </div>

                <div
                    class="mt-16 bg-iron-100 md:grid-cols-3 border-iron-100 grid gap-px border"
                >
                    <div
                        v-for="(step, i) in steps"
                        :key="step.title"
                        class="bg-white p-8"
                    >
                        <p
                            class="font-serif text-iron-300 text-5xl leading-none"
                        >
                            {{ String(i + 1).padStart(2, "0") }}
                        </p>
                        <h3 class="mt-6 text-base font-medium text-iron-900">
                            {{ step.title }}
                        </h3>
                        <p class="mt-2 text-sm text-iron-600 leading-relaxed">
                            {{ step.body }}
                        </p>
                    </div>
                </div>
            </div>
        </section>

        <!-- FEATURES -->
        <section
            id="funkce"
            class="px-6 py-24 max-w-6xl border-iron-100 mx-auto border-t"
        >
            <div class="max-w-2xl">
                <p
                    class="text-xs font-medium tracking-widest text-iron-400 uppercase"
                >
                    Funkce
                </p>
                <h2
                    class="mt-4 font-serif font-normal tracking-tight text-iron-900 leading-tight text-4xl sm:text-5xl"
                >
                    Co dostanete.
                </h2>
                <p class="mt-6 text-lg text-iron-600 leading-relaxed">
                    Všechno, co potřebujete k tomu, aby si klient sám
                    zarezervoval prohlídku — a nic, co byste museli sami
                    nastavovat.
                </p>
            </div>

            <div
                class="gap-x-12 gap-y-12 mt-16 sm:grid-cols-2 lg:grid-cols-3 grid"
            >
                <div
                    v-for="(feature, i) in features"
                    :key="feature.title"
                    class="border-iron-100 pt-6 border-t"
                >
                    <p class="text-xs tracking-wider text-iron-400">
                        {{ String(i + 1).padStart(2, "0") }}
                    </p>
                    <h3 class="mt-3 text-base font-medium text-iron-900">
                        {{ feature.title }}
                    </h3>
                    <p class="mt-2 text-sm text-iron-600 leading-relaxed">
                        {{ feature.description }}
                    </p>
                </div>
            </div>
        </section>

        <!-- PORTFOLIO PREVIEW -->
        <section class="bg-iron-50 px-6 py-24 border-iron-100 border-y">
            <div class="max-w-6xl mx-auto">
                <div class="max-w-2xl">
                    <p
                        class="text-xs font-medium tracking-widest text-iron-400 uppercase"
                    >
                        Bonus
                    </p>
                    <h2
                        class="mt-4 font-serif font-normal tracking-tight text-iron-900 leading-tight text-4xl sm:text-5xl"
                    >
                        Vaše veřejné portfolio.
                    </h2>
                    <p class="mt-6 text-lg text-iron-600 leading-relaxed">
                        Kromě rezervační stránky pro každou nemovitost získáváte
                        také společnou stránku se všemi vašimi aktivními
                        inzeráty.
                    </p>
                </div>

                <div class="gap-6 mt-16 sm:grid-cols-2 lg:grid-cols-3 grid">
                    <div
                        v-for="card in portfolioCards"
                        :key="card.addr"
                        class="bg-white border-iron-200 overflow-hidden border"
                    >
                        <img
                            :src="card.img"
                            :alt="card.addr"
                            class="h-48 w-full object-cover"
                        />
                        <div class="p-5">
                            <p class="font-serif text-lg text-iron-900">
                                {{ card.price }}
                            </p>
                            <p
                                class="gap-1.5 mt-2 text-sm text-iron-700 flex items-center"
                            >
                                <MapPin class="h-3.5 w-3.5 text-iron-400" />
                                {{ card.addr }}
                            </p>
                            <p class="mt-1 text-xs text-iron-500">
                                {{ card.m2 }} · {{ card.rooms }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- PERSONAS -->
        <section class="px-6 py-24 max-w-6xl mx-auto">
            <div class="max-w-2xl">
                <p
                    class="text-xs font-medium tracking-widest text-iron-400 uppercase"
                >
                    Pro koho je to
                </p>
                <h2
                    class="mt-4 font-serif font-normal tracking-tight text-iron-900 leading-tight text-4xl sm:text-5xl"
                >
                    Dva typy makléřů.
                </h2>
            </div>

            <div
                class="mt-16 bg-iron-100 md:grid-cols-2 border-iron-100 grid gap-px border"
            >
                <div class="bg-white p-10">
                    <p
                        class="text-xs font-medium tracking-widest text-iron-400 uppercase"
                    >
                        Solo
                    </p>
                    <h3 class="mt-3 font-serif text-2xl text-iron-900">
                        Samostatní makléři.
                    </h3>
                    <p class="mt-5 text-base text-iron-600 leading-relaxed">
                        Reality nahradí chaos SMSek a WhatsAppů jednou
                        rezervační stránkou. Klient si sám vybere čas, vy
                        dostanete potvrzení. Žádné nastavování, žádné integrace.
                    </p>
                    <ul class="gap-3 mt-6 text-sm text-iron-700 flex flex-col">
                        <li class="gap-3 flex items-start">
                            <span
                                class="bg-iron-300 mt-3 w-6 h-px flex-shrink-0"
                            ></span>
                            Připojení kalendáře za minutu
                        </li>
                        <li class="gap-3 flex items-start">
                            <span
                                class="bg-iron-300 mt-3 w-6 h-px flex-shrink-0"
                            ></span>
                            E-maily a potvrzení už napsané
                        </li>
                        <li class="gap-3 flex items-start">
                            <span
                                class="bg-iron-300 mt-3 w-6 h-px flex-shrink-0"
                            ></span>
                            Vlastní odkaz pro každý inzerát
                        </li>
                    </ul>
                </div>

                <div class="bg-white p-10">
                    <p
                        class="text-xs font-medium tracking-widest text-iron-400 uppercase"
                    >
                        Síť
                    </p>
                    <h3 class="mt-3 font-serif text-2xl text-iron-900">
                        Makléři pod značkou.
                    </h3>
                    <p class="mt-5 text-base text-iron-600 leading-relaxed">
                        Reality žije vedle vašeho firemního CRM, ne místo něj.
                        Slouží jen k jedné věci: dostat klienta na prohlídku
                        rychleji, s vaší vlastní rezervační stránkou.
                    </p>
                    <ul class="gap-3 mt-6 text-sm text-iron-700 flex flex-col">
                        <li class="gap-3 flex items-start">
                            <span
                                class="bg-iron-300 mt-3 w-6 h-px flex-shrink-0"
                            ></span>
                            Nezasahuje do systému kanceláře
                        </li>
                        <li class="gap-3 flex items-start">
                            <span
                                class="bg-iron-300 mt-3 w-6 h-px flex-shrink-0"
                            ></span>
                            Vaše značka, váš odkaz, váš telefon
                        </li>
                        <li class="gap-3 flex items-start">
                            <span
                                class="bg-iron-300 mt-3 w-6 h-px flex-shrink-0"
                            ></span>
                            Kontakty se nikam nesynchronizují
                        </li>
                    </ul>
                </div>
            </div>
        </section>

        <!-- PRICING -->
        <section id="cena" class="px-6 py-24 border-iron-100 border-t">
            <div class="max-w-6xl mx-auto">
                <div class="max-w-2xl">
                    <p
                        class="text-xs font-medium tracking-widest text-iron-400 uppercase"
                    >
                        Cena
                    </p>
                    <h2
                        class="mt-4 font-serif font-normal tracking-tight text-iron-900 leading-tight text-4xl sm:text-5xl"
                    >
                        Jednoduchá cena.
                    </h2>
                    <p class="mt-6 text-lg text-iron-600 leading-relaxed">
                        Začněte zdarma s plnou funkčností. Zaplatíte teprve, až
                        vám začnou rezervovat skuteční klienti.
                    </p>
                </div>

                <div
                    class="mt-16 bg-iron-100 md:grid-cols-2 max-w-4xl border-iron-100 grid gap-px border"
                >
                    <div class="bg-white p-10">
                        <p
                            class="text-xs font-medium tracking-widest text-iron-400 uppercase"
                        >
                            Pro
                        </p>
                        <p class="mt-2 text-sm text-iron-500">
                            Pro samostatné makléře
                        </p>
                        <p class="mt-8 font-serif text-iron-900 text-5xl">
                            690 Kč
                        </p>
                        <p class="mt-1 text-sm text-iron-500">
                            za měsíc · až 10 nemovitostí
                        </p>

                        <a
                            :href="ctaHref"
                            class="border-iron-900 px-4 py-3 text-sm font-medium text-iron-900 hover:bg-iron-900 hover:text-white mt-8 inline-flex w-full items-center justify-center rounded-full border transition-colors"
                        >
                            Vyzkoušet zdarma
                        </a>

                        <ul
                            class="gap-3 mt-10 text-sm text-iron-700 flex flex-col"
                        >
                            <li
                                v-for="item in proFeatures"
                                :key="item"
                                class="gap-3 flex items-start"
                            >
                                <span
                                    class="bg-iron-300 mt-3 w-4 h-px flex-shrink-0"
                                ></span>
                                {{ item }}
                            </li>
                        </ul>
                    </div>

                    <div class="bg-white p-10 relative">
                        <span
                            class="text-xs font-medium tracking-widest text-iron-400 right-10 top-10 absolute uppercase"
                        >
                            Doporučeno
                        </span>
                        <p
                            class="text-xs font-medium tracking-widest text-iron-400 uppercase"
                        >
                            Business
                        </p>
                        <p class="mt-2 text-sm text-iron-500">
                            Pro makléře s plným portfoliem
                        </p>
                        <p class="mt-8 font-serif text-iron-900 text-5xl">
                            1 490 Kč
                        </p>
                        <p class="mt-1 text-sm text-iron-500">
                            za měsíc · neomezeně nemovitostí
                        </p>

                        <a
                            :href="ctaHref"
                            class="bg-iron-900 px-4 py-3 text-sm font-medium text-white hover:bg-iron-800 mt-8 inline-flex w-full items-center justify-center rounded-full transition-colors"
                        >
                            Vyzkoušet zdarma
                        </a>

                        <ul
                            class="gap-3 mt-10 text-sm text-iron-700 flex flex-col"
                        >
                            <li
                                v-for="item in businessFeatures"
                                :key="item"
                                class="gap-3 flex items-start"
                            >
                                <span
                                    class="bg-iron-300 mt-3 w-4 h-px flex-shrink-0"
                                ></span>
                                {{ item }}
                            </li>
                        </ul>
                    </div>
                </div>

                <p class="mt-8 text-sm text-iron-500 max-w-4xl">
                    Všechny plány lze kdykoliv zrušit. Zkušební režim je plně
                    funkční — rezervace přijímá pouze pro 3 testovací e-maily,
                    které si určíte.
                </p>
            </div>
        </section>

        <!-- FAQ -->
        <section
            id="faq"
            class="bg-iron-50 px-6 py-24 border-iron-100 border-y"
        >
            <div class="max-w-3xl mx-auto">
                <p
                    class="text-xs font-medium tracking-widest text-iron-400 uppercase"
                >
                    Dotazy
                </p>
                <h2
                    class="mt-4 font-serif font-normal tracking-tight text-iron-900 leading-tight text-4xl sm:text-5xl"
                >
                    Časté dotazy.
                </h2>

                <Accordion type="single" collapsible class="mt-12 w-full">
                    <AccordionItem
                        v-for="(item, i) in faqItems"
                        :key="i"
                        :value="'faq-' + i"
                        class="border-iron-200 border-b"
                    >
                        <AccordionTrigger
                            class="text-base font-medium text-iron-900 py-6 text-left hover:no-underline"
                        >
                            {{ item.q }}
                        </AccordionTrigger>
                        <AccordionContent
                            class="text-base text-iron-600 leading-relaxed pb-6"
                        >
                            {{ item.a }}
                        </AccordionContent>
                    </AccordionItem>
                </Accordion>
            </div>
        </section>

        <!-- FINAL CTA + ACCESS GATE -->
        <section id="pristup" class="px-6 py-32 max-w-4xl mx-auto text-center">
            <h2
                class="font-serif font-normal tracking-tight text-iron-900 text-4xl sm:text-6xl leading-[1.05]"
            >
                Začněte přijímat<br />
                <span class="text-iron-500 italic">rezervace do hodiny.</span>
            </h2>
            <p
                class="mt-8 text-lg text-iron-600 max-w-xl leading-relaxed mx-auto"
            >
                Plná funkčnost. Bez karty. Bez časového limitu.
            </p>

            <div v-if="accessGranted" class="mt-12">
                <a
                    :href="route('register')"
                    class="bg-iron-900 px-8 py-4 text-sm font-medium text-white hover:bg-iron-800 inline-flex items-center justify-center rounded-full transition-colors"
                >
                    Vyzkoušet zdarma
                </a>
            </div>

            <div v-else class="mt-12 max-w-md mx-auto">
                <p class="mb-5 text-sm text-iron-500">
                    Reality je v uzavřeném beta provozu. Pro registraci zadejte
                    přístupový kód.
                </p>
                <form
                    class="gap-2 sm:flex-row flex flex-col"
                    @submit.prevent="submitAccessCode"
                >
                    <input
                        v-model="form.access_code"
                        type="password"
                        autocomplete="off"
                        required
                        placeholder="Přístupový kód"
                        class="border-iron-200 bg-white px-5 py-3 text-sm text-iron-900 placeholder:text-iron-400 focus:border-iron-900 block w-full rounded-full border transition-colors focus:outline-none"
                        :disabled="form.processing"
                    />
                    <button
                        type="submit"
                        class="bg-iron-900 px-6 py-3 text-sm font-medium text-white hover:bg-iron-800 rounded-full whitespace-nowrap transition-colors disabled:opacity-60"
                        :disabled="form.processing"
                    >
                        Odemknout
                    </button>
                </form>
                <p
                    v-if="form.errors.access_code"
                    class="mt-3 text-sm text-red-600"
                >
                    {{ form.errors.access_code }}
                </p>
            </div>
        </section>
    </RealityMarketingLayout>
</template>
